import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { PlainInput } from 'components/PlainInput'
import { AccountType, userHasAccountType } from 'config'
import { DateTime } from 'luxon'
import { overrideValidationKeyOptions } from 'pages/LoanStructure/constant'
import { useEffect, useState } from 'react'
import { confirmable, createConfirmation, ReactConfirmProps } from 'react-confirm'
import { store } from 'reducers'
import {
  getACHLog,
  getAdditionalPropertyInfoLogs,
  getAppraisal2Logs,
  getAssetTrackLogs,
  getBorrowerLogs,
  getCollateralReviewLogs,
  getFundingDisbursementLogs,
  getHUD1Logs,
  getLoanDetailLog,
  getLoanLogs,
  getLoanProcessLogs,
  getLoanSubmissionLogs,
  getNYTransactionsLogs,
  getPurchaseAdviceLogs,
  getPurchaseAdviceSellerLogs,
  getServicingBorrowingBaseLog,
  getServicingDefaultTrackingLog,
  getShippingInfoHeaderLog,
  getShippingInfoLogs,
  getUnderwritingSummaryLogs,
  getUserLogs,
  updateLoanProcessLog,
} from 'services'
import {
  downloadAuditLogCSV,
  downloadAuditLogPDF,
  filterMasterEmails,
  getAccountingHistory,
  getBorrowerFieldLogs,
  getBudgetTemplateLogs,
  getConfigFieldLogs,
  getContractorLogs,
  getCreditDenialLogs,
  getGenerateDocumentLog,
  getHmdaLogs,
  getInvestorLoanStatusHistory,
  getInvoiceLogs,
  getLoanEstimateLogs,
  getLoanPaymentLogs,
  getLoanStructureHistory,
  getLoanSubmissionChecklistLogs,
  getMailLogs,
  getMersLogs,
  getPartyLogs,
  getPermissionLogs,
  getRealEstateOwnedLogs,
  getReviewFormLogs,
  getServicngNotesLogs,
  getTrustLedgerAdditionalHistory,
  getVendorLogs,
} from 'services/apis'
import { getLoanValidationLogs } from 'services/apis/loan/validation'
import { getReminderLogs } from 'services/apis/reminder'
import { getTaskFieldLog } from 'services/apis/task'
import { getUserAllFieldLogs } from 'services/apis/user'
import { Modal, Tooltip } from 'stories/components'
import { formatTime, removeDuplicates } from 'utils/convertor'

interface UserAuditLogProps extends ReactConfirmProps {
  options: {
    table: string
    field: string
    keys: any
    map?: any
    renderValue?: Function | Record<string, string> | string[]
    fieldLabel?: string
    childLevel?: number
  }
}

interface IAuditLogItem {
  action?: string
  email: string
  time: Date
  value: any
  detail?: any
}

const rateLockAuditActions = new Set(['Lock Rate', 'ReLock Rate', 'Float Rate'])
const rateLockFieldKeys = ['rateLocked', 'lockedDate', 'expireDate', 'lockDays', 'extensions']

const isRateLockAuditEntry = (entry: any) => {
  const action = String(entry?.action || '')
  if (rateLockAuditActions.has(action)) return true
  if (action.startsWith('Lock Extension') || action.startsWith('Rate Lock')) return true
  if (action === 'Override Pricing Values' && entry?.detail && typeof entry.detail === 'object') {
    return rateLockFieldKeys.some((key) => Object.prototype.hasOwnProperty.call(entry.detail, key))
  }
  return false
}

const formatRateLockAuditValue = (log: any) => {
  const action = String(log?.action || '')
  const detail = log?.detail
  if (action === 'Override Pricing Values' && detail && typeof detail === 'object') {
    const parts = rateLockFieldKeys
      .filter((key) => Object.prototype.hasOwnProperty.call(detail, key))
      .map((key) => {
        const value = detail[key]
        const shown =
          value == null || value === ''
            ? '(empty)'
            : Array.isArray(value)
            ? value.join(', ') || '(none)'
            : String(value)
        return `${key}: ${shown}`
      })
    if (parts.length) return `${action} (${parts.join(', ')})`
  }
  if (action.startsWith('Lock Extension') && detail?.expireDate) return `${action} (expires ${detail.expireDate})`
  return action
}

const AuditLogDialog = ({ show, proceed, options }: UserAuditLogProps) => {
  const { auth } = store.getState()

  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  const [auditLog, setAuditLog] = useState<IAuditLogItem[]>([])
  const [fetchingData, setFetchingData] = useState(true)
  const isAdmin = userHasAccountType(auth.profile, AccountType.ADMIN)
  const [isEditable, setEditable] = useState(false)
  const [visibleEmails, setVisibleEmails] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLastUpdatedAt(Date.now())
  }, [show])

  useEffect(() => {
    const { table, keys } = options
    setLoading(true)
    if (table === 'LoanSubmission') {
      getLoanSubmissionLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'LoanSubmissionChecklist') {
      getLoanSubmissionChecklistLogs(keys.no, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'FundingDisbursement') {
      getFundingDisbursementLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'User') {
      getUserLogs(keys.userId, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'UserAllField') {
      getUserAllFieldLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Loan') {
      getLoanLogs(keys.field, keys?.propertyId).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'LoanDetails') {
      const field = keys.fieldKey
      if (!field) {
        setAuditLog([])
        setLoading(false)
        return
      }
      getLoanDetailLog(field, keys.section, keys.recordKey).then((data) => {
        setAuditLog(
          (data || [])
            .filter(
              (item: any) =>
                item.fieldKey === field &&
                (!keys.section || item.section === keys.section) &&
                (!keys.recordKey || item.recordKey === keys.recordKey),
            )
            .map((item: any) => ({
              value: item.newValue,
              email: item.actorEmail,
              time: item.createdAt,
            })),
        )
        setLoading(false)
      })
    }
    if (table === 'Loan Validation') {
      getLoanValidationLogs(keys.field, keys?.type, keys?.index).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'LoanProcess') {
      getLoanProcessLogs(keys.field, keys.loanNumber)
        .then((data) => {
          const mapped = (data ?? []).map((item: any) => ({
            ...item,
            value: Array.isArray(item.value)
              ? item.value.map((entry: any) => ({
                  ...entry,
                  type: overrideValidationKeyOptions[entry.type] ?? entry.type,
                }))
              : item.value,
          }))
          setAuditLog(mapped)
          setEditable(isAdmin)
          setLoading(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
    if (table === 'Borrower') {
      getBorrowerLogs(keys.borrowerSeperator, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'ManageBorrower') {
      getBorrowerFieldLogs(keys.borrowerId, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Appraisal2') {
      getAppraisal2Logs(keys.field, keys.id).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Contractor') {
      getContractorLogs(keys.no, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'AssetTrack') {
      getAssetTrackLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'PurchaseBuyer') {
      getPurchaseAdviceLogs(keys.type, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'PurchaseSeller') {
      getPurchaseAdviceSellerLogs(keys.type, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Underwriting') {
      getUnderwritingSummaryLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'NYTransactions') {
      getNYTransactionsLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'ServicingDefaultTracking') {
      getServicingDefaultTrackingLog(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Ach') {
      getACHLog(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'ShippingInfo') {
      getShippingInfoLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'ShippingInfoHeader') {
      getShippingInfoHeaderLog(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'CollateralReview') {
      getCollateralReviewLogs(keys.menu, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'AdditionalPropertyInformation') {
      getAdditionalPropertyInfoLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'HUD1') {
      getHUD1Logs(keys.field).then((data) => {
        setAuditLog(Array.isArray(data) ? data : [])
        setLoading(false)
      })
    }
    if (table === 'TrustLedger') {
      getTrustLedgerAdditionalHistory(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'GenerateDocument') {
      getGenerateDocumentLog(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'AccountingRecords') {
      getAccountingHistory(keys.id).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'InvestorLoan') {
      getInvestorLoanStatusHistory(keys.investorID, keys.loanNo).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Reminder') {
      getReminderLogs(keys.no, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Task') {
      getTaskFieldLog(keys.taskId, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Servicing Information') {
      getServicngNotesLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'ServicingBorrowingBase') {
      getServicingBorrowingBaseLog(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Invoice') {
      getInvoiceLogs(keys.id, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'LoanPayment') {
      getLoanPaymentLogs(keys.id, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Mail') {
      getMailLogs(keys.id, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'HMDA') {
      getHmdaLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'LoanEstimate') {
      getLoanEstimateLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'MERS') {
      getMersLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Statement of Credit Denial') {
      getCreditDenialLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Valuation Review') {
      getReviewFormLogs(keys.id, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'RealEstateOwned') {
      getRealEstateOwnedLogs(keys.key, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Party') {
      getPartyLogs(keys.loanNumber, keys.field, keys.partyName).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Permission') {
      getPermissionLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'ConfigField') {
      getConfigFieldLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Budget') {
      getBudgetTemplateLogs(keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Vendor') {
      getVendorLogs(keys.type, keys.field).then((data) => {
        setAuditLog(data)
        setLoading(false)
      })
    }
    if (table === 'Lock History') {
      getLoanStructureHistory().then((data) => {
        const auditLog = (data || []).filter((v: any) => isRateLockAuditEntry(v))
        auditLog.forEach((v: any) => {
          v.time = new Date(v.createdAt).getTime()
        })
        setAuditLog(auditLog)
        setLoading(false)
      })
    }
  }, [options])

  useEffect(() => {
    const emails = removeDuplicates(auditLog.map((v) => v.email))
    if (!emails.length) {
      setFetchingData(false)
      return
    }

    filterByMaster(emails)
  }, [auditLog])

  const filterByMaster = async (emails: string[]) => {
    setLoading(true)
    const filteredEmails = await filterMasterEmails(emails)
    setVisibleEmails(filteredEmails)
    setFetchingData(false)
    setLoading(false)
  }

  const onUpdate = async (type: string, newValue: string, index: number) => {
    let value = newValue
    if (type === 'time') {
      value = DateTime.fromJSDate(new Date(newValue)).toISO() || ''
    }
    const { keys, table } = options
    if (table === 'LoanProcess') {
      const newData = cloneDeep(auditLog)
      ;(newData[index] as any)[type] = value
      setAuditLog(newData)

      setFetchingData(true)
      await updateLoanProcessLog(keys.field, keys.loanNumber, {
        index,
        type,
        value,
      })
      setFetchingData(false)
    }
  }

  const replaceMap = (logs: IAuditLogItem[]) => {
    const newLogs = cloneDeep(logs)
    newLogs.map((log) => {
      let value = renderLogValue(log)
      value = value?.props?.dangerouslySetInnerHTML?.__html || value
      value = value.replace ? value.replace(/<br\s*\/?>/g, '\n') : value
      log.value = value
    })
    return newLogs
  }

  const renderLogValue = (log: IAuditLogItem) => {
    let value = null
    if (options.map) {
      value = options.map[log.value]
    } else if (options.renderValue) {
      if (typeof options.renderValue === 'function') {
        value = options.renderValue(log.value)
      } else {
        try {
          if (Array.isArray(options.renderValue)) {
            value = options.renderValue.find((option: string) => option === log.value)
          } else {
            value = options.renderValue[log.value]
          }
        } catch (e) {}
      }
    } else if (options.table === 'Lock History') {
      value = formatRateLockAuditValue(log)
    } else {
      if (typeof log.value === 'boolean') {
        value = log.value ? 'Yes' : 'No'
      } else {
        value = JSON.stringify(log.value)
      }
    }
    if (!value) value = JSON.stringify(log.value)

    return value
  }

  const onDownloadCSV = () => {
    const { table, keys, fieldLabel } = options
    downloadAuditLogCSV(replaceMap(auditLog), table, fieldLabel || keys.field).finally(() => {})
  }
  const onDownloadPDF = () => {
    const { table, keys, fieldLabel } = options
    downloadAuditLogPDF(
      { auditLog: replaceMap(auditLog), loanNumber: keys.loanNumber },
      table,
      fieldLabel || keys.field,
    ).finally(() => {})
  }

  return (
    <Modal
      button={<span></span>}
      title={'Audit Log Data Details'}
      titleOkay=""
      init={fetchingData}
      isOpen={show}
      lastUpdatedAt={lastUpdatedAt}
      onClose={() => proceed(false as any)}
      childLevel={options.childLevel ?? 1}
      loading={loading}
    >
      <>
        <div className="flex justify-between">
          <div className="info-container text-sm ml-3 max-w-screen-xl">
            <div className="mb-3">
              <span>Table:</span>
              <span className="ml-3 font-bold">{options.table}</span>
            </div>
            {options.keys?.propertyId && (
              <div className="mb-3">
                <span>Property:</span>
                <span className="ml-3 font-bold">Property {options.keys.propertyIndex + 2}</span>
              </div>
            )}
            <div className="mb-4">
              <span>Field:</span>
              <span className="ml-3 font-bold">
                {options.fieldLabel || options.field}
                {options.table === 'Loan Validation' && options.keys?.index !== undefined && options.keys?.index !== -1
                  ? ` (Property ${options.keys.index + 2})`
                  : ''}
              </span>
            </div>
          </div>
          <div className="flex items-center">
            <span
              className="p-1 mr-2 text-sm cursor-pointer text-shade-blue hover-shadow1 rounded"
              onClick={onDownloadCSV}
            >
              <Tooltip message="Download CSV" placement="right">
                <ArrowDownTrayIcon className="w-4 h-4" />
              </Tooltip>
            </span>
            <span className="p-1 text-sm cursor-pointer text-shade-blue hover-shadow1 rounded" onClick={onDownloadPDF}>
              <Tooltip message="Download PDF" placement="right">
                <ArrowDownTrayIcon className="w-4 h-4" />
              </Tooltip>
            </span>
          </div>
        </div>
        <div className="relative overflow-x-auto shadow-md sm:rounded-lg max-w-screen-xl">
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-6 py-3">
                  No
                </th>
                <th scope="col" className="px-6 py-3 w-48">
                  Date & Time
                </th>
                <th scope="col" className="px-6 py-3">
                  Email
                </th>
                <th scope="col" className="px-6 py-3">
                  New Value
                </th>
              </tr>
            </thead>
            <tbody className="text-gray-900">
              {auditLog
                .filter((v) => visibleEmails.includes(v.email))
                .map((log, index) => (
                  <tr key={index} className={`${index % 2 && 'bg-gray-50'}`}>
                    <td scope="row" className="px-6 py-2">
                      {index + 1}
                    </td>
                    <td className="px-6 py-2">
                      {!isEditable ? (
                        formatTime(log.time)
                      ) : (
                        <PlainInput
                          value={formatTime(log.time)}
                          content={formatTime(log.time)}
                          onChange={(newValue: string) => onUpdate('time', newValue, index)}
                        />
                      )}
                    </td>
                    <td className="px-6 py-2">{log.email}</td>
                    <td className="px-6 py-2">{renderLogValue(log)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </>
    </Modal>
  )
}

const auditLogDlg = confirmable(AuditLogDialog)

const createAuditLogDlg = createConfirmation(auditLogDlg)

export const openAuditLog = (options: Record<string, any> = {}) => createAuditLogDlg({ options })

/** Open audit history for a company-wide admin configuration field. */
export const openConfigFieldAuditLog = (fieldKey: string, fieldLabel: string, childLevel = 1) =>
  openAuditLog({
    table: 'ConfigField',
    field: fieldLabel,
    fieldLabel,
    keys: { field: fieldKey },
    childLevel,
  })
